import { Modal, notification, Spin } from "antd";
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from "react-router-dom";
import toggleClosed from '../../assets/image/toggle_close.png';
import toggleOpen from '../../assets/image/toggle_open.png';
import { useAuth } from '../../component/auth/AuthContext';
import { getUser, updatePassword, updateUser } from "../../store/user/action";
import { apiErrorText } from "../../utils/apiError";
import './ProfileStyled.scss';
import profileImage from '../../assets/image/profile02.png';

const Profile = () => {
  const { t } = useTranslation();


  const Auth = useAuth();
  const user = Auth.getUser();
  const username = user ? user.data.username : '';

  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [isPasswordChanging, setIsPasswordChanging] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showNewPasswordConfirmation, setShowNewPasswordConfirmation] = useState(false);
  const [userInfo, setUserInfo] = useState({});
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirmation, setNewPasswordConfirmation] = useState('');
  const [passwordMatchMessage, setPasswordMatchMessage] = useState('');
  const [currentPasswordMessage, setCurrentPasswordMessage] = useState('');
  const [newPasswordMessage, setNewPasswordMessage] = useState('');
  const [hasAttemptedCurrentPassword, setHasAttemptedCurrentPassword] = useState(false);
  const [hasAttemptedNewPassword, setHasAttemptedNewPassword] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);

  const getUserInfo = async (username) => {
    try {
      // console.log("Fetching user info for username:", username);
      const response = await getUser({ username });
      // console.log("User info response:", response);
      if (response && typeof response === 'object') {
        setUserInfo(response);
      } else {
        console.warn("No user info received");
        setUserInfo({});
      }
    } catch (error) {
      console.error("Error fetching user info:", error);
      setUserInfo({});
    } finally {
      setPageLoading(false);
    }
  };

  useEffect(() => {
    if (username) {
      getUserInfo(username);
    }
  }, [username]);

  useEffect(() => {
    if (hasAttemptedCurrentPassword) {
      setCurrentPasswordMessage(currentPassword ? '' : 'current password is required');
    }
    if (hasAttemptedNewPassword) {
      setNewPasswordMessage(newPassword ? '' : 'new password is required');
    }
  }, [currentPassword, newPassword, hasAttemptedCurrentPassword, hasAttemptedNewPassword]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'company') {
      setUserInfo((prevInfo) => ({
        ...prevInfo,
        [name]: value,
      }));
    } else if (name === 'currentPassword') {
      setCurrentPassword(value);
      setHasAttemptedCurrentPassword(true);
    } else if (name === 'newPassword') {
      setNewPassword(value);
      setHasAttemptedNewPassword(true);
      if (newPasswordConfirmation && value !== newPasswordConfirmation) {
        setPasswordMatchMessage("password do not match");
      } else if (newPasswordConfirmation) {
        setPasswordMatchMessage("password match");
      } else {
        setPasswordMatchMessage('');
      }
    } else if (name === 'newPasswordConfirmation') {
      setNewPasswordConfirmation(value);
      if (value !== newPassword) {
        setPasswordMatchMessage("password do not match");
      } else {
        setPasswordMatchMessage("password match");
      }
    }
  };

  const saveUserInfo = async () => {
    try {
      const response = await updateUser(userInfo);
      if (response.status === 200) {
        notification.success({
          message: '성공',
          description: '사용자 정보가 성공적으로 업데이트되었습니다.',
          placement: 'top',
          duration: 3,
        });
        setIsEditing(false);
      } else {
        notification.error({
          message: '실패',
          description: '사용자 정보 업데이트에 실패했습니다.',
          placement: 'top',
          duration: 3,
        });
      }
    } catch (error) {
      notification.error({
        message: '실패',
        description: '사용자 정보 저장에 실패했습니다.',
        placement: 'top',
        duration: 3,
      });
    }
  };

  const validatePasswordFields = () => {
    if (!currentPassword) {
      setCurrentPasswordMessage('current password is required');
      return false;
    }
    if (!newPassword) {
      setNewPasswordMessage('new password is required');
      return false;
    }
    if (newPassword !== newPasswordConfirmation) {
      setPasswordMatchMessage('password do not match');
      return false;
    }
    return true;
  };

  const logout = () => {
    Auth.userLogout();
  }

  const showConfirm = (cTitle, cContent, onOkFunction) => {
    Modal.confirm({
      title: cTitle,
      content: cContent,
      okText: 'confirm',
      cancelText: 'cancel',
      onOk() {
        onOkFunction()
      },
      onCancel() {
        console.log("cancelled");
      },
    });
  };

  const handlePasswordUpdate = async () => {

    try {
      const pcInfo = {
        ...userInfo,
        currentPassword: currentPassword,
        newPassword: newPassword
      };
      
      const response = await updatePassword(pcInfo);
      
      if (response.status === 200) {
        logout();
        navigate('/login');
      } else if (response.status === 400) {
        // 이 분기는 오랫동안 죽어 있다가, store/api/action.js 의 onFailure 가
        // 오류 본문을 흘려보내게 되면서 살아났습니다. response.message 를 그대로
        // 쓰면 백엔드의 영문 원문이 한국어 화면에 그대로 박힙니다.
        setCurrentPasswordMessage(apiErrorText(response, 'err.currentPasswordIncorrect'));
      } else {
        notification.error({
          message: '실패',
          description: '비밀번호 업데이트에 실패했습니다.',
          placement: 'top',
          duration: 3,
        });
      }
    } catch (error) {
      notification.error({
        message: '실패',
        description: '비밀번호 업데이트에 실패했습니다.',
      });
    }
  };

  // const openNotificationWithIcon = (type, message) => {
  //   api[type]({
  //     description: message,
  //   });
  // };

  const toggleEdit = () => {
    if (isPasswordChanging) {
      setIsPasswordChanging(false);
    }
    if (isEditing) {
      showConfirm(
        "save changes?",
        "do you want to save your changes?",
        saveUserInfo
      );
    } else {
      setIsEditing(true);
    }
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setIsPasswordChanging(false);
    setUserInfo(userInfo);
  };

  const togglePasswordChange = () => {
    setIsPasswordChanging(!isPasswordChanging);
  };

  const togglePasswordVisibility = (field) => {
    switch (field) {
      case 'currentPassword':
        setShowCurrentPassword(!showCurrentPassword);
        break;
      case 'newPassword':
        setShowNewPassword(!showNewPassword);
        break;
      case 'newPasswordConfirmation':
        setShowNewPasswordConfirmation(!showNewPasswordConfirmation);
        break;
      default:
        break;
    }
  };

  return (
    <div>
      <div className="page-title p-16">{t('pageTitle.profile')}</div>
      <div className="user-container">
        {/* Notice 표시할 위치 */}
        {/* {contextHolder} */}
        <div className="user-info-section">
          <div className="user-info-header">
            <h1>{userInfo.username}</h1>
            {isEditing ? (
              <>
                <button className="save-button" onClick={toggleEdit}>
                  SAVE
                </button>
                <button className="cancel-button" onClick={cancelEdit}>
                  CANCEL
                </button>
              </>
            ) : (
              <button className="edit-button" onClick={toggleEdit}>
                EDIT
              </button>
            )}
          </div>
          <div className="user-info-details">
            <div className="user-info-item">
              <label>Username&nbsp;&nbsp;</label>
              <span className="user-info-value">{userInfo.username}</span>
            </div>
            <div className="user-info-item">
              <label>Email&nbsp;&nbsp;</label>
              <span className="user-info-value">{userInfo.email}</span>
            </div>
            <div className="user-info-item">
              <label>Group&nbsp;&nbsp;</label>
              <span className="user-info-value">{userInfo.groupName || 'No Group'}</span>
            </div>
            {/* <div className="user-info-item">
              <label>Language&nbsp;&nbsp;&nbsp;</label>
              <span className="user-info-value">{userInfo.language}</span>
            </div> */}
            <div className="user-info-item">
              <label>Report Count&nbsp;&nbsp;</label>
              <span className="user-info-value">{userInfo.reportCount}</span>
            </div>
            <div className="user-info-item">
              <label>Company&nbsp;&nbsp;</label>
              {isEditing ? (
                <input
                  type="text"
                  name="company"
                  value={userInfo.company}
                  onChange={handleInputChange}
                  className="user-input"
                  placeholder="company"
                />
              ) : (
                <span className="user-info-value">{userInfo.company}</span>
              )}
            </div>
            <div className="user-info-item">
              {isPasswordChanging ? (
                <div className="password-change-container">
                  <div className="password-change-item">
                    <label>Current Password</label>
                    <div className="password-input-container">
                      <input
                        type={showCurrentPassword ? 'text' : 'password'}
                        name="currentPassword"
                        value={currentPassword}
                        onChange={handleInputChange}
                        className="user-input"
                        placeholder="enter the current password"
                      />
                      <img
                        src={showCurrentPassword ? toggleOpen : toggleClosed}
                        alt="toggle visibility"
                        className="toggle-password"
                        onClick={() => togglePasswordVisibility('currentPassword')}
                      />
                    </div>
                    {currentPasswordMessage && (
                      <p className="errorMessage">{currentPasswordMessage}</p>
                    )}
                  </div>
                  <div className="password-change-item">
                    <label>New Password</label>
                    <div className="password-input-container">
                      <input
                        type={showNewPassword ? 'text' : 'password'}
                        name="newPassword"
                        value={newPassword}
                        onChange={handleInputChange}
                        className="user-input"
                        placeholder="enter a new password"
                      />
                      <img
                        src={showNewPassword ? toggleOpen : toggleClosed}
                        alt="toggle visibility"
                        className="toggle-password"
                        onClick={() => togglePasswordVisibility('newPassword')}
                      />
                    </div>
                    {newPasswordMessage && (
                      <p className="errorMessage">{newPasswordMessage}</p>
                    )}
                  </div>
                  <div className="password-change-item">
                    <label>New Password Confirmation</label>
                    <div className="password-input-container">
                      <input
                        type={showNewPasswordConfirmation ? 'text' : 'password'}
                        name="newPasswordConfirmation"
                        value={newPasswordConfirmation}
                        onChange={handleInputChange}
                        className="user-input"
                        placeholder="enter a new password again"
                      />
                      <img
                        src={showNewPasswordConfirmation ? toggleOpen : toggleClosed}
                        alt="toggle visibility"
                        className="toggle-password"
                        onClick={() => togglePasswordVisibility('newPasswordConfirmation')}
                      />
                    </div>
                    <p className={passwordMatchMessage === "password match" ? 'matchMessage' : 'errorMessage'}>
                      {passwordMatchMessage}
                    </p>
                  </div>
                  <div className="password-change-buttons">
                    <button
                      className="ok-button password-btn"
                      onClick={() => {
                        if (validatePasswordFields()) {
                          showConfirm(
                            "change your password?",
                            "after changes, login again.",
                            handlePasswordUpdate
                          );
                        }
                      }}
                    >
                      SAVE
                    </button>
                    <button className="cancel-button password-btn" onClick={togglePasswordChange}>
                      CANCEL
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {isEditing && (
                    <button className="modify-button" onClick={togglePasswordChange}>
                      CHANGE PASSWORD
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
        <div className="user-image-section">
          <div className="image-overlay-text">
            <div>Profile Information</div>
            <div style={{ 
              fontSize: '16px', 
              fontWeight: 'normal', 
              lineHeight: '1.6',
              textAlign: 'left',
              width: '100%'
            }}>
              이 화면에서는 다음과 같은 작업이 가능합니다:<br/>
              • Company 정보 수정<br/>
              • 비밀번호 변경<br/>
              (비밀번호 변경 시 자동 로그아웃되며,<br/>
              새로운 비밀번호로 재로그인이 필요합니다)
            </div>
          </div>
          <img 
            src={profileImage} 
            alt="Profile" 
            className="rotating-profile-image"
          />
        </div>
      </div>
      <Modal
        open={pageLoading}
        footer={null}
        closable={false}
        centered
        width={300}
      >
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Spin size="large" />
          <p style={{ marginTop: '10px' }}>로딩 중...</p>
        </div>
      </Modal>
    </div>
  );
};

export default Profile;
